import { useFilters } from "../useFilters";

/**
 * Hook spécifique pour gérer les filtres des demandes de pièces (replenishments)
 * @param resetPageCallback - Fonction pour réinitialiser la pagination
 * @returns - Interface pour manipuler les filtres
 */
export function useReplenishmentsFilters(
  resetPageCallback: () => void
) {
  // Fonction pour transformer les filtres avant sérialisation
  const transformForSerialization = (filters: Partial<EasyTech.Replenishment>) => {
    const filtersCopy = { ...filters };

    // Traitement spécial pour les dates
    if (filtersCopy.startDate) {
      const startValue = filtersCopy.startDate as any;
      const dateString = startValue instanceof Date
        ? startValue.toISOString()
        : new Date(startValue).toISOString();
      filtersCopy.startDate = dateString as any;
    }
    if (filtersCopy.endDate) {
      const endValue = filtersCopy.endDate as any;
      const dateString = endValue instanceof Date
        ? endValue.toISOString()
        : new Date(endValue).toISOString();
      filtersCopy.endDate = dateString as any;
    }
    return filtersCopy;
  };

  // Fonction pour transformer les filtres après désérialisation
  const transformForDeserialization = (decodedFilters: any): Partial<EasyTech.Replenishment> => {
    const filters = { ...decodedFilters };

    // Migration des anciens statuts vers les nouveaux
    if (filters.status) {
      const statusMigration: Record<string, EasyTech.ReplenishmentStatus> = {
        "PENDING": "CREATED",
        "IN_PROGRESS": "SENT",
        "COMPLETED": "INVENTORIED"
        // CANCELLED reste CANCELLED
      };

      if (statusMigration[filters.status]) {
        filters.status = statusMigration[filters.status];
      }
    }

    // Traitement spécial pour les dates - on les garde en format string
    // pour éviter les erreurs de type, elles seront converties en Date au moment
    // de leur utilisation dans les requêtes API
    if (filters.startDate) {
      // Garder en string, pas de conversion en Date pour éviter les erreurs de type
      filters.startDate = filters.startDate as any;
    }

    if (filters.endDate) {
      // Garder en string, pas de conversion en Date pour éviter les erreurs de type
      filters.endDate = filters.endDate as any;
    }

    if (filters.technician && Array.isArray(filters.technician)) {
      filters.technician = filters.technician.filter((id: any) => !!id);
    }
    return filters;
  };

  // Combine startDate et endDate en un seul objet dateRange pour l'affichage en badge unique
  const transformForDisplay = (filters: Partial<EasyTech.Replenishment>): Record<string, any> => {
    const displayFilters: Record<string, any> = {};

    Object.entries(filters).forEach(([key, value]) => {
      if (key !== "startDate" && key !== "endDate") {
        displayFilters[key] = value;
      }
    });

    if (filters.startDate || filters.endDate) {
      displayFilters.dateRange = {
        start: filters.startDate || null,
        end: filters.endDate || null
      };
    }

    return displayFilters;
  };

  return useFilters<Partial<EasyTech.Replenishment>>(
    resetPageCallback,
    {
      transformForSerialization,
      transformForDeserialization,
      transformForDisplay
    }
  );
}
